import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";

const PaymentTable = ({ pevData, setIsEdit, isEdit, setNewData, newData = [], setIsSaveChanges, isSaveChanges }) => {  // Ensure newData has a default value

    const [instalments, setInstalments] = useState(pevData.instalments || []);
    // const [isEditing, setIsEditing] = useState(false);

    const { pdfData, setPdfData ,langMode } = useContext(AppContext);

    const totalPaymentAmount = instalments.reduce((sum, inst) => sum + (parseFloat(inst.paymentAmount) || 0), 0);
    const amountToAdjust = parseFloat(pevData.outstandingAfterAdvance) - totalPaymentAmount;

    useEffect(() => {
        if (!isEdit && instalments) {
            setPdfData(prev => ({
                ...prev,
                instalments: instalments,
                currentOutstanding: pevData.currentOutstanding,
                outstandingAfterAdvance: pevData.outstandingAfterAdvance
            }));
            const formattedData = instalments.map(inst => ({
                paymentDate: new Date(inst.paymentDate).toISOString(),
                paymentAmount: parseFloat(inst.paymentAmount) || 0
            }));
            setNewData(formattedData)
        }
    }, [instalments, isEdit]);

    const handleAmountChange = (index, value) => {
        let newAmount = parseFloat(value) || 0;
        let newInstalments = [...instalments];

        const totalOutstanding = parseFloat(pevData.outstandingAfterAdvance);

        let newTotalPaymentAmount = instalments.reduce((sum, inst, i) =>
            sum + (i === index ? newAmount : parseFloat(inst.paymentAmount) || 0), 0
        );

        if (newAmount >= totalOutstanding) {
            toast.error("You cannot pay the full amount in one installment. Please distribute across all installments.");
            return;
        }

        let remainingInstallments = instalments.length - 1;
        let remainingBalance = totalOutstanding - newAmount;

        if (remainingBalance / remainingInstallments < 1) {
            toast.error(`Each installment must have at least a minimum amount.`);
            return;
        }

        newInstalments[index].paymentAmount = newAmount;
        const remainingIndexes = newInstalments
            .map((_, i) => i)
            .filter(i => i !== index);

        if (remainingIndexes.length > 0) {
            const lastIndex = remainingIndexes[remainingIndexes.length - 1];
            // Get total paymentAmount of all other remainingIndexes (except last)
            const totalOfOthers = remainingIndexes
                .filter(i => i !== lastIndex)
                .reduce((sum, i) => sum + (parseFloat(newInstalments[i].paymentAmount) || 0), 0);
        
            // Get the final amount for the last index
            const finalAmount = parseFloat((remainingBalance - totalOfOthers));
        
            // Set final amount to last index
            newInstalments[lastIndex].paymentAmount = finalAmount;
        }

        setInstalments(newInstalments);

        newTotalPaymentAmount = newInstalments.reduce((sum, inst) => sum + (parseFloat(inst.paymentAmount) || 0), 0);
        const updatedAmountToAdjust = totalOutstanding - newTotalPaymentAmount;

        setIsEdit(true);

        setPdfData(prev => ({
            ...prev,
            instalments: newInstalments,
            isEMIMismatch: Math.abs(updatedAmountToAdjust) > 0.01,
        }));
    };

    const handleEditClick = () => {
        setIsSaveChanges(true);
        setIsEdit(true);
    };

    const handleSaveClick = () => {
        console.log("handleSaveClick function triggered!");

        const formattedData = instalments.map(inst => ({
            paymentDate: new Date(inst.paymentDate).toISOString(),
            paymentAmount: parseFloat(inst.paymentAmount) || 0
        }));

        console.log("Saved Data:", formattedData);

        setPdfData(prev => ({
            ...prev,
            instalments: instalments,
            currentOutstanding: pevData.currentOutstanding,
            outstandingAfterAdvance: pevData.outstandingAfterAdvance,
            isEMIMismatch: false, // Ensure button is enabled after saving
        }));

        setNewData(formattedData);

        setIsSaveChanges(false); // Enable the button after saving

        // setIsEdit(false);
        toast.success("Changes saved successfully!");
    };

    return (
        <div className="p-4">
            {totalPaymentAmount < 0 && (
                <div className={`font-semibold mb-2 text-red-600`}>
                    {
                        `You have entered more than the total outstanding balance by ${Math.abs(totalPaymentAmount).toFixed(2)}. Please reduce the EMI amount.`
                    }
                </div>
            )}
            {Math.abs(amountToAdjust) > 0.01 && (
                <div className={`font-semibold mb-2 ${amountToAdjust > 0 ? "text-red-600" : "text-blue-600"}`}>
                    {amountToAdjust > 0 ?
                        `You have entered less EMI amount. Please adjust ${amountToAdjust.toFixed(2)} more to match the outstanding balance.`
                        :
                        `You have entered more than the total outstanding balance by ${Math.abs(amountToAdjust).toFixed(2)}. Please reduce the EMI amount.`}
                </div>
            )}

            <div className="mt-4 flex justify-between">
                <h2 className="text-md font-semibold mb-2">{translations[langMode].payDetail}</h2>
                {!isSaveChanges ? (
                    <button className="px-4 py-2 bg-blue-500 rounded-md text-white" onClick={handleEditClick}>
                       {translations[langMode].edit}
                    </button>
                ) : (
                    <button className="px-4 py-2 bg-green-500 text-white rounded-md" onClick={handleSaveClick}>
                          {translations[langMode].save}
                    </button>
                )}
            </div>

            <table className="w-full text-sm border-collapse border border-gray-300 mt-4">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-4 py-2">{translations[langMode].AfterAdv}</th>
                        <th className="border border-gray-300 px-4 py-2">{translations[langMode].currentOut}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="text-center">
                        <td className="border border-gray-300 px-4 py-2">{totalPaymentAmount < 0 ? 0 : pevData.outstandingAfterAdvance}</td>
                        <td className="border border-gray-300 px-4 py-2">{pevData.currentOutstanding}</td>
                    </tr>
                </tbody>
            </table>

            <table className="w-full text-sm border-collapse border border-gray-300 mt-4">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-4 py-2">{translations[langMode].payDate}</th>
                        <th className="border border-gray-300 px-4 py-2">{translations[langMode].Amount}</th>
                        <th className="border border-gray-300 px-4 py-2">{translations[langMode].Notes}</th>
                    </tr>
                </thead>
                <tbody>
                    {instalments.map((inst, index) => (
                        <tr key={index} className="text-center">
                            <td className="border border-gray-300 px-4 py-2">
                                {new Date(inst.paymentDate).toLocaleDateString()}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                                {!isSaveChanges ? (
                                    <span>{totalPaymentAmount < 0 ? 0 : inst.paymentAmount}</span>
                                ) : (
                                    <input
                                        type="number"
                                        value={inst.paymentAmount}
                                        step="0.01"
                                        className="w-full px-2 py-1 border rounded"
                                        onChange={(e) => handleAmountChange(index, e.target.value)}
                                    />
                                )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{inst.notes || "N/A"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PaymentTable;
